import { useState } from "react";
import { Heart, BookOpen, Feather, Users, ExternalLink, Mail } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const TIERS = [
  { amount: 25, title: "One Reader", desc: "Sponsors books for one child for a season." },
  { amount: 50, title: "A Classroom", desc: "Provides reading materials for a small group." },
  { amount: 100, title: "A Young Author", desc: "Helps a child publish their first book." },
  { amount: 250, title: "Library Champion", desc: "Stocks the WHO DAT BarberShop library shelves." },
];

// Replace with the foundation's external donation URL (e.g., PayPal/GoFundMe) when provided.
const EXTERNAL_DONATE_URL = "https://www.paypal.com/donate";

export default function Donate() {
  const [contact, setContact] = useState({ name: "", email: "", subject: "Partnership inquiry", message: "" });
  const [sending, setSending] = useState(false);

  const submitContact = async (e) => {
    e.preventDefault();
    if (!contact.name || !contact.email || !contact.message) {
      toast.error("Name, email, and message are required");
      return;
    }
    try {
      setSending(true);
      await api.post("/contact", contact);
      toast.success("Thank you — we'll be in touch soon!");
      setContact({ name: "", email: "", subject: "Partnership inquiry", message: "" });
    } catch {
      toast.error("Could not send. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <section className="container-wrap pt-16 md:pt-20 pb-12">
        <p className="overline mb-5"><span className="gold-divider align-middle mr-3" />Give</p>
        <h1 className="font-serif-display text-5xl md:text-6xl text-brand-dark max-w-4xl leading-[1.0]">
          Your gift writes a child's future.
        </h1>
        <p className="mt-6 max-w-2xl text-brand-dark/70 text-lg leading-relaxed">
          Every dollar goes directly to free books, author-led sessions, and the Young Authors program. We're a 501(c)(3) nonprofit — your contribution is tax-deductible.
        </p>
      </section>

      <section className="container-wrap pb-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {TIERS.map((t) => (
            <a
              key={t.amount}
              href={`${EXTERNAL_DONATE_URL}?amount=${t.amount}`}
              target="_blank"
              rel="noreferrer"
              data-testid={`donate-tier-${t.amount}`}
              className="group rounded-3xl bg-white border border-brand-purple/15 p-7 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all flex flex-col"
            >
              <p className="font-serif-display text-5xl text-brand-purple group-hover:text-brand-gold transition">${t.amount}</p>
              <p className="font-serif-display text-xl text-brand-dark mt-2">{t.title}</p>
              <p className="text-brand-dark/65 text-sm mt-2 flex-1">{t.desc}</p>
              <p className="mt-5 text-sm font-semibold text-brand-purple group-hover:text-brand-gold-soft inline-flex items-center gap-1">
                Donate <ExternalLink className="w-3.5 h-3.5" />
              </p>
            </a>
          ))}
        </div>

        <div className="mt-8 text-center">
          <a
            href={EXTERNAL_DONATE_URL}
            target="_blank"
            rel="noreferrer"
            data-testid="donate-custom-button"
            className="btn-primary inline-flex"
          >
            <Heart className="w-4 h-4 mr-2" /> Donate Any Amount
          </a>
        </div>
      </section>

      {/* Impact */}
      <section className="bg-brand-lavender/50 py-20">
        <div className="container-wrap">
          <p className="overline mb-4"><span className="gold-divider align-middle mr-3" />Impact</p>
          <h2 className="font-serif-display text-4xl md:text-5xl text-brand-dark mb-10 max-w-2xl leading-tight">Where your dollars go.</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: BookOpen, t: "Free Books", d: "1,000+ distributed across New Orleans neighborhoods." },
              { icon: Feather, t: "Young Authors", d: "Kids becoming published authors with their names on the cover." },
              { icon: Users, t: "Community", d: "A safe library inside the WHO DAT BarberShop, open to all kids." },
            ].map(({ icon: Icon, t, d }, i) => (
              <div key={i} className="bg-white rounded-3xl p-7 border border-brand-purple/15">
                <Icon className="w-7 h-7 text-brand-gold mb-4" />
                <h3 className="font-serif-display text-2xl text-brand-dark mb-2">{t}</h3>
                <p className="text-brand-dark/70">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Other ways + Contact */}
      <section className="container-wrap py-20">
        <div className="grid md:grid-cols-2 gap-12">
          <div>
            <p className="overline mb-4"><span className="gold-divider align-middle mr-3" />Other Ways to Give</p>
            <h2 className="font-serif-display text-3xl md:text-4xl text-brand-dark mb-6 leading-tight">Beyond a one-time gift.</h2>
            <ul className="space-y-4 text-brand-dark/80">
              <li className="flex gap-3"><span className="text-brand-gold font-bold">+</span> <span><strong className="text-brand-dark">Donate books.</strong> Drop new or gently used children's books at the WHO DAT BarberShop.</span></li>
              <li className="flex gap-3"><span className="text-brand-gold font-bold">+</span> <span><strong className="text-brand-dark">Corporate sponsorship.</strong> Partner with us on author events, school visits, and book drives.</span></li>
              <li className="flex gap-3"><span className="text-brand-gold font-bold">+</span> <span><strong className="text-brand-dark">Volunteer.</strong> Read with kids, mentor a young author, help at an event.</span></li>
              <li className="flex gap-3"><span className="text-brand-gold font-bold">+</span> <span><strong className="text-brand-dark">Legacy giving.</strong> Include WHO DAT Cares in your estate planning.</span></li>
            </ul>
          </div>

          <div className="bg-brand-dark text-white rounded-3xl p-8 md:p-10">
            <div className="flex items-center gap-3 mb-2">
              <Mail className="w-5 h-5 text-brand-gold" />
              <p className="overline text-brand-gold">Get in Touch</p>
            </div>
            <h3 className="font-serif-display text-3xl mb-2">Have an idea? Tell us.</h3>
            <p className="text-white/70 mb-6">Sponsorships, partnerships, donations, or saying hi — we read every message.</p>
            <form onSubmit={submitContact} className="space-y-3">
              <input required value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} placeholder="Your name" data-testid="contact-name-input" className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-sm placeholder:text-white/50 focus:outline-none focus:border-brand-gold" />
              <input required type="email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} placeholder="Email" data-testid="contact-email-input" className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-sm placeholder:text-white/50 focus:outline-none focus:border-brand-gold" />
              <input value={contact.subject} onChange={(e) => setContact({ ...contact, subject: e.target.value })} placeholder="Subject" data-testid="contact-subject-input" className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-sm placeholder:text-white/50 focus:outline-none focus:border-brand-gold" />
              <textarea required rows={4} value={contact.message} onChange={(e) => setContact({ ...contact, message: e.target.value })} placeholder="Tell us how you'd like to help…" data-testid="contact-message-input" className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-sm placeholder:text-white/50 focus:outline-none focus:border-brand-gold" />
              <button type="submit" disabled={sending} data-testid="contact-submit-button" className="bg-brand-gold text-brand-dark hover:bg-brand-gold-soft font-semibold rounded-full px-6 py-3 transition w-full disabled:opacity-60">
                {sending ? "Sending…" : "Send Message"}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
